import { useState, useCallback } from 'react';
import api from '../api';
import {
  CalendarRange, RefreshCw, Download, FileText, AlertTriangle, ShieldAlert,
} from 'lucide-react';
import toast from 'react-hot-toast';
import Can from './Can';

// ----------------------------------------------------------------
// Title 31 — MTL log + CTR register over a date range
// ----------------------------------------------------------------
// Everything else in Title 31 is one gaming day at a time, which is right for
// the cage but wrong for a review or an examiner walkthrough. This is the range
// view: the MTL log and the CTR register as two sections, per-day totals, CSV
// download, and a batch XML export covering the whole span.
//
// PII: rows carry the patron NAME and the SSN LAST FOUR only, because that is
// all the API returns here. The full SSN lives behind POST /patrons/:id/reveal,
// which needs its own permission and writes an access-log row before it
// answers. Nothing on this screen — or in the CSV it produces — routes around
// that.
// ----------------------------------------------------------------

const fmtCents = (c) =>
  c === null || c === undefined
    ? '—'
    : `${c < 0 ? '-' : ''}$${Math.abs(Math.round(c) / 100).toLocaleString('en-US', {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      })}`;

const todayYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const daysAgoYmd = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const apiErr = (e, fallback) =>
  toast.error(e?.response?.data?.error || e?.message || fallback);

// RFC4180-ish: quote everything and double internal quotes. Names carry commas
// and apostrophes, and a mis-quoted compliance export is worse than no export.
const csvCell = (v) => `"${String(v === null || v === undefined ? '' : v).replace(/"/g, '""')}"`;
const csvRow = (cells) => cells.map(csvCell).join(',');
// Dollars as a plain number so the column sums in a spreadsheet.
const csvMoney = (c) => (Number(c || 0) / 100).toFixed(2);

export default function Title31RangeReport() {
  const [from, setFrom] = useState(daysAgoYmd(30));
  const [to, setTo] = useState(todayYmd());
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportResult, setExportResult] = useState(null);
  const [tab, setTab] = useState('mtl');

  const run = useCallback(async () => {
    setLoading(true);
    setExportResult(null);
    try {
      const { data } = await api.get('/admin/title31/report', { params: { from, to } });
      setRep(data);
      if (!data.totals.sheets && !data.totals.ctrs) {
        toast('Nothing logged in that range.', { icon: 'ℹ️' });
      }
    } catch (e) {
      setRep(null);
      apiErr(e, 'Could not build the report');
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  const downloadCsv = () => {
    if (!rep) return;
    const lines = [];
    lines.push(csvRow(['EDH Casino — Title 31 report']));
    lines.push(csvRow([`Gaming days ${rep.fromDate} through ${rep.toDate} (${rep.days} days)`]));
    lines.push(csvRow([`Generated ${new Date().toISOString()}`]));
    lines.push(csvRow([
      `CTR threshold ${fmtCents(rep.thresholds.ctrThresholdCents)} (exceeds, per gaming day)`,
    ]));
    lines.push(csvRow(['SSN shown as last four only. Full values require the permissioned reveal.']));
    lines.push('');

    lines.push(csvRow(['SUMMARY']));
    lines.push(csvRow(['Gaming date', 'Sheets', 'Flagged for CTR', 'CTRs', 'Cash in', 'Cash out']));
    for (const d of rep.byDay) {
      lines.push(csvRow([d.gamingDate, d.sheets, d.flaggedForCtr, d.ctrs,
        csvMoney(d.cashInCents), csvMoney(d.cashOutCents)]));
    }
    lines.push(csvRow(['TOTAL', rep.totals.sheets, rep.totals.flaggedForCtr, rep.totals.ctrs,
      csvMoney(rep.totals.cashInCents), csvMoney(rep.totals.cashOutCents)]));
    lines.push('');

    lines.push(csvRow(['MULTIPLE TRANSACTION LOG']));
    lines.push(csvRow([
      'Gaming date', 'Patron', 'SSN last 4', 'ID type', 'ID issuer', 'Table', 'Seat',
      'Transactions', 'Cash in', 'Cash out', 'Flagged for CTR', 'Trigger', 'Signed', 'CTR status',
    ]));
    for (const r of rep.mtl) {
      lines.push(csvRow([
        r.gamingDate, r.name || '(unidentified)', r.tinLast4 || '',
        r.idType || '', r.idIssuer || '', r.tableNumber || '', r.seatNumber || '',
        r.transactionCount, csvMoney(r.totalCashInCents), csvMoney(r.totalCashOutCents),
        r.flaggedForCtr ? 'YES' : '', r.triggerType || '',
        r.signedAt ? 'yes' : 'no', r.ctrStatus || '',
      ]));
    }
    lines.push('');

    lines.push(csvRow(['CTR REGISTER']));
    lines.push(csvRow([
      'Gaming date', 'Patron', 'Trigger', 'Cash in', 'Cash out', 'Status',
      'Batch', 'Exported at', 'Filed at', 'BSA ID',
    ]));
    for (const c of rep.ctrs) {
      lines.push(csvRow([
        c.gamingDate, c.personName || '(unidentified)', c.triggerType || '',
        csvMoney(c.cashInCents), csvMoney(c.cashOutCents), c.status,
        c.batchId || '', c.exportedAt || '', c.filedAt || '', c.bsaId || '',
      ]));
    }

    // BOM so Excel opens UTF-8 names correctly rather than mangling them.
    const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `EDH-Title31-${rep.fromDate}_to_${rep.toDate}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success('Report downloaded');
  };

  const exportRangeBatch = async () => {
    if (!rep) return;
    setExporting(true);
    setExportResult(null);
    try {
      const { data } = await api.post('/admin/title31/ctrs/export', {
        fromDate: rep.fromDate, toDate: rep.toDate,
      });
      setExportResult(data);
      if (data.ok && data.xml) {
        const blob = new Blob([data.xml], { type: 'application/xml' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = data.filename || 'CTRX_batch.xml';
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        toast.success(`Batch exported — ${data.activityCount} CTR(s), ${data.coversFrom} to ${data.coversTo}`);
        await run();
      } else {
        toast.error('Export blocked — resolve the validation errors below');
      }
    } catch (e) {
      apiErr(e, 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const inp = 'rounded border border-gray-600 bg-gray-900 px-2.5 py-1.5 text-sm text-white focus:border-amber-500 focus:outline-none';

  return (
    <div className="rounded-xl border border-gray-700 bg-gray-800 p-5">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-center gap-2 font-semibold text-white">
          <CalendarRange size={18} className="text-amber-400" /> MTL &amp; CTR report
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-xs text-gray-400">
            <span className="mb-1 block">From</span>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={inp} />
          </label>
          <label className="text-xs text-gray-400">
            <span className="mb-1 block">To</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={inp} />
          </label>
          <button
            onClick={run}
            disabled={loading}
            className="flex items-center gap-1.5 rounded bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-500 disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Run
          </button>
          {rep && (
            <button
              onClick={downloadCsv}
              className="flex items-center gap-1.5 rounded border border-gray-600 px-3 py-1.5 text-sm text-gray-200 hover:bg-gray-700"
            >
              <Download size={14} /> CSV
            </button>
          )}
        </div>
      </div>

      {!rep && (
        <p className="text-sm text-gray-500">
          Pick a range and run it. Everything else in Title 31 is one gaming day at a time;
          this is the view for a review or an examiner walkthrough.
        </p>
      )}

      {rep && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            <Tile label="Sheets" value={rep.totals.sheets} sub={`${rep.days} gaming days`} />
            <Tile label="Cash in" value={fmtCents(rep.totals.cashInCents)} sub={`${rep.totals.transactions} transactions`} />
            <Tile label="Cash out" value={fmtCents(rep.totals.cashOutCents)} />
            <Tile label="Flagged for CTR" value={rep.totals.flaggedForCtr}
              accent={rep.totals.flaggedForCtr ? 'amber' : null} />
            <Tile label="CTRs" value={rep.totals.ctrs}
              sub={rep.totals.ctrsUnfiled ? `${rep.totals.ctrsUnfiled} not filed` : 'all filed'}
              accent={rep.totals.ctrsUnfiled ? 'red' : null} />
          </div>

          {/* An unsigned sheet and an unfiled CTR are the two things a review is
              actually looking for, so they are called out rather than left to be
              noticed in a table. */}
          {(rep.totals.unsigned > 0 || rep.totals.ctrsUnfiled > 0) && (
            <div className="mt-3 flex flex-wrap gap-3">
              {rep.totals.ctrsUnfiled > 0 && (
                <Flag icon={ShieldAlert} tone="red">
                  {rep.totals.ctrsUnfiled} CTR{rep.totals.ctrsUnfiled === 1 ? '' : 's'} not yet filed.
                  FinCEN allows 15 calendar days from the gaming date.
                </Flag>
              )}
              {rep.totals.unsigned > 0 && (
                <Flag icon={AlertTriangle} tone="amber">
                  {rep.totals.unsigned} sheet{rep.totals.unsigned === 1 ? '' : 's'} unsigned.
                </Flag>
              )}
            </div>
          )}

          <div className="mt-4 flex gap-1 border-b border-gray-700">
            {[['mtl', `MTL log (${rep.mtl.length})`], ['ctrs', `CTR register (${rep.ctrs.length})`], ['days', `By day (${rep.byDay.length})`]].map(([k, label]) => (
              <button
                key={k}
                onClick={() => setTab(k)}
                className={`px-3 py-1.5 text-sm ${tab === k ? 'border-b-2 border-amber-500 font-medium text-amber-300' : 'text-gray-400 hover:text-gray-200'}`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="mt-3 max-h-[28rem] overflow-auto">
            {tab === 'mtl' && <MtlTable rows={rep.mtl} />}
            {tab === 'ctrs' && <CtrTable rows={rep.ctrs} />}
            {tab === 'days' && <DayTable rows={rep.byDay} />}
          </div>

          <Can permission="title31:file">
            <div className="mt-4 rounded-lg border border-gray-700 bg-gray-900 p-3">
              <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-gray-200">
                <FileText size={15} /> FinCEN batch for this range
              </h3>
              <button
                onClick={exportRangeBatch}
                disabled={exporting}
                className="flex w-full items-center justify-center gap-2 rounded bg-amber-600 px-3 py-2 text-sm font-medium text-white hover:bg-amber-500 disabled:opacity-50"
              >
                <Download size={15} /> {exporting ? 'Building…' : `Export CTR batch XML — ${rep.fromDate} to ${rep.toDate}`}
              </button>
              <p className="mt-2 text-[11px] text-gray-500">
                One batch file covering the whole range. A FinCEN batch is a transmission
                container, not a daily document, so it may carry CTRs from several gaming
                dates. CTRs already exported or filed are skipped, so this cannot double-file.
                Validate in the FinCEN test environment before your first real filing.
              </p>
              {exportResult && <ExportResult r={exportResult} />}
            </div>
          </Can>
        </>
      )}
    </div>
  );
}

function Tile({ label, value, sub, accent }) {
  const tone = accent === 'red' ? 'text-red-300' : accent === 'amber' ? 'text-amber-300' : 'text-white';
  return (
    <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className={`mt-0.5 text-xl font-semibold tabular-nums ${tone}`}>{value}</div>
      {sub ? <div className="mt-0.5 text-[11px] text-gray-500">{sub}</div> : null}
    </div>
  );
}

function Flag({ icon: Icon, tone, children }) {
  const cls = tone === 'red'
    ? 'border-red-800 bg-red-950/40 text-red-200'
    : 'border-amber-800 bg-amber-950/40 text-amber-200';
  return (
    <div className={`flex items-start gap-2 rounded border px-3 py-2 text-xs ${cls}`}>
      <Icon size={14} className="mt-0.5 shrink-0" />
      <span>{children}</span>
    </div>
  );
}

const TH = ({ children, right }) => (
  <th className={`py-2 pr-3 text-xs font-medium uppercase tracking-wide text-gray-500 ${right ? 'text-right' : 'text-left'}`}>{children}</th>
);

function MtlTable({ rows }) {
  if (!rows.length) return <Empty>No MTL sheets in this range.</Empty>;
  return (
    <table className="w-full text-sm">
      <thead className="sticky top-0 bg-gray-800">
        <tr className="border-b border-gray-700">
          <TH>Date</TH><TH>Patron</TH><TH>SSN</TH><TH>ID</TH><TH>Table</TH>
          <TH right>Txns</TH><TH right>Cash in</TH><TH right>Cash out</TH><TH>CTR</TH><TH>Signed</TH>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id} className={`border-b border-gray-800 ${r.flaggedForCtr ? 'bg-amber-950/20' : ''}`}>
            <td className="py-1.5 pr-3 tabular-nums text-gray-300">{r.gamingDate}</td>
            <td className="py-1.5 pr-3 text-gray-100">{r.name || <span className="text-gray-500">(unidentified)</span>}</td>
            <td className="py-1.5 pr-3 tabular-nums text-gray-400">{r.tinLast4 ? `••••${r.tinLast4}` : '—'}</td>
            <td className="py-1.5 pr-3 text-xs text-gray-400">{r.idType || '—'}</td>
            <td className="py-1.5 pr-3 text-gray-400">{r.tableNumber || '—'}</td>
            <td className="py-1.5 pr-3 text-right tabular-nums text-gray-400">{r.transactionCount}</td>
            <td className="py-1.5 pr-3 text-right tabular-nums text-gray-200">{fmtCents(r.totalCashInCents)}</td>
            <td className="py-1.5 pr-3 text-right tabular-nums text-gray-200">{fmtCents(r.totalCashOutCents)}</td>
            <td className="py-1.5 pr-3 text-xs">
              {r.flaggedForCtr
                ? <span className="rounded bg-amber-500/20 px-1.5 py-0.5 text-amber-300">{r.ctrStatus || 'needed'}</span>
                : <span className="text-gray-600">—</span>}
            </td>
            <td className="py-1.5 text-xs">{r.signedAt ? <span className="text-emerald-400">yes</span> : <span className="text-amber-400">no</span>}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CtrTable({ rows }) {
  if (!rows.length) return <Empty>No CTRs in this range.</Empty>;
  return (
    <table className="w-full text-sm">
      <thead className="sticky top-0 bg-gray-800">
        <tr className="border-b border-gray-700">
          <TH>Date</TH><TH>Patron</TH><TH>Trigger</TH><TH right>Cash in</TH><TH right>Cash out</TH>
          <TH>Status</TH><TH>Batch</TH><TH>BSA ID</TH>
        </tr>
      </thead>
      <tbody>
        {rows.map((c) => {
          const open = c.status !== 'filed' && c.status !== 'acknowledged';
          return (
            <tr key={c.id} className={`border-b border-gray-800 ${open ? 'bg-red-950/20' : ''}`}>
              <td className="py-1.5 pr-3 tabular-nums text-gray-300">{c.gamingDate}</td>
              <td className="py-1.5 pr-3 text-gray-100">{c.personName || <span className="text-gray-500">(unidentified)</span>}</td>
              <td className="py-1.5 pr-3 text-xs text-gray-400">{c.triggerType || '—'}</td>
              <td className="py-1.5 pr-3 text-right tabular-nums text-gray-200">{fmtCents(c.cashInCents)}</td>
              <td className="py-1.5 pr-3 text-right tabular-nums text-gray-200">{fmtCents(c.cashOutCents)}</td>
              <td className="py-1.5 pr-3 text-xs">
                <span className={open ? 'text-red-300' : 'text-emerald-400'}>{c.status}</span>
              </td>
              <td className="py-1.5 pr-3 text-[11px] text-gray-500">{c.batchId || '—'}</td>
              <td className="py-1.5 text-[11px] text-gray-500">{c.bsaId || '—'}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function DayTable({ rows }) {
  if (!rows.length) return <Empty>Nothing logged in this range.</Empty>;
  return (
    <table className="w-full text-sm">
      <thead className="sticky top-0 bg-gray-800">
        <tr className="border-b border-gray-700">
          <TH>Gaming date</TH><TH right>Sheets</TH><TH right>Flagged</TH><TH right>CTRs</TH>
          <TH right>Cash in</TH><TH right>Cash out</TH>
        </tr>
      </thead>
      <tbody>
        {rows.map((d) => (
          <tr key={d.gamingDate} className="border-b border-gray-800">
            <td className="py-1.5 pr-3 tabular-nums text-gray-300">{d.gamingDate}</td>
            <td className="py-1.5 pr-3 text-right tabular-nums text-gray-200">{d.sheets}</td>
            <td className={`py-1.5 pr-3 text-right tabular-nums ${d.flaggedForCtr ? 'text-amber-300' : 'text-gray-500'}`}>{d.flaggedForCtr}</td>
            <td className="py-1.5 pr-3 text-right tabular-nums text-gray-200">{d.ctrs}</td>
            <td className="py-1.5 pr-3 text-right tabular-nums text-gray-200">{fmtCents(d.cashInCents)}</td>
            <td className="py-1.5 text-right tabular-nums text-gray-200">{fmtCents(d.cashOutCents)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const Empty = ({ children }) => <div className="p-4 text-sm text-gray-500">{children}</div>;

function ExportResult({ r }) {
  return (
    <div className="mt-3 space-y-2 text-xs">
      {!r.ok && (
        <div className="rounded border border-red-800 bg-red-950/50 p-2">
          <div className="mb-1 font-medium text-red-300">Export blocked — nothing was written</div>
          {(r.validations || []).flatMap((v) => v.errors || []).map((e, i) => (
            <div key={i} className="text-red-200">• {e}</div>
          ))}
        </div>
      )}
      {r.ok && (
        <div className="rounded border border-emerald-800 bg-emerald-950/40 p-2 text-emerald-200">
          Exported {r.activityCount} CTR(s) covering {r.coversFrom} to {r.coversTo}
          {r.gamingDates && r.gamingDates.length > 1 ? ` (${r.gamingDates.length} gaming dates)` : ''},
          {' '}{r.partyCount} parties, total ${r.totalAmount}. Batch {r.batchId}.
        </div>
      )}
      {(r.validations || []).some((v) => (v.warnings || []).length) && (
        <div className="rounded border border-amber-800 bg-amber-950/40 p-2">
          <div className="mb-1 font-medium text-amber-300">Warnings</div>
          {(r.validations || []).flatMap((v) => v.warnings || []).map((w, i) => (
            <div key={i} className="text-amber-200">• {w}</div>
          ))}
        </div>
      )}
    </div>
  );
}
